/**
 * Notifications — cross-tenant feed: status/severity/tenant filters, the
 * console's only paged endpoint, acknowledge, deep-link-to-tenant, and the
 * credential-invalid remedy (WSRPDM-0015, contract §4).
 *
 * Status `all` is never sent — the contract rejects `status=all` with 400
 * `INVALID_STATUS` (§4.1); the "All" tab omits the param entirely
 * (`listNotifications`'s `status: undefined`, see `src/api/wsr/
 * notifications.ts`). Changing ANY filter resets `page` to 0 — a stale page
 * index against a narrower filter is an easy off-by-one otherwise.
 *
 * Unlike Queue, this screen does not poll — a single fetch per
 * filter/page/reload change, mirroring `TenantDetailPanel`'s reload-token
 * pattern. The tenant `<select>`'s slug options are derived from the
 * currently-loaded page (no separate roster call, same tradeoff Queue.tsx
 * makes for its tenant filter) — a tenant whose only open notification sits
 * on a later page won't appear in the list until that page is seen; noted in
 * the delivery.
 */
import { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { useCopy } from '@/hooks/useCopy';
import { useSetNavBadge } from '@/hooks/useNavBadges';
import { acknowledge, listNotifications } from '@/api/wsr/notifications';
import { wsrApiError } from '@/api/wsr/errors';
import { FilterTabs } from '@/components/ui';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import type { NotificationStatus, PlatformNotificationView } from '@/api/wsr/types';
import { NotificationRow } from './notifications/NotificationRow';

const PAGE_SIZE = 20;

type StatusTab = 'ALL' | NotificationStatus;
type SeverityFilter = 'ALL' | 'INFO' | 'WARN' | 'ERROR';

export function Notifications() {
  const { t } = useCopy();

  const [statusTab, setStatusTab] = useState<StatusTab>('OPEN');
  const [severityFilter, setSeverityFilter] = useState<SeverityFilter>('ALL');
  const [tenantFilter, setTenantFilter] = useState('ALL');
  const [page, setPage] = useState(0);

  const [data, setData] = useState<{
    content: PlatformNotificationView[];
    totalPages: number;
    totalElements: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  const [actionError, setActionError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  // Main feed fetch — stable deps only (no `t`, no inline objects/callbacks),
  // see usePolling.ts's/useTenantRoster.ts's doc comments for why an
  // unstable dep here would refetch on every render.
  useEffect(() => {
    let cancelled = false;
    listNotifications({
      status: statusTab === 'ALL' ? undefined : statusTab,
      severity: severityFilter === 'ALL' ? undefined : severityFilter,
      tenant: tenantFilter === 'ALL' ? undefined : tenantFilter,
      page,
      size: PAGE_SIZE,
    })
      .then((res) => {
        if (cancelled) return;
        setData(res);
        setLoadFailed(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoadFailed(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [statusTab, severityFilter, tenantFilter, page, reloadToken]);

  // Nav badge — a single light call (size 1), independent of the screen's
  // own filters/page, wired through the shared useSetNavBadge (WSRPDM-0011)
  // rather than a second mechanism. Refreshed alongside the main feed
  // (`reloadToken`) so acknowledging a row updates the count too.
  const [openCount, setOpenCount] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    listNotifications({ status: 'OPEN', size: 1 })
      .then((res) => {
        if (!cancelled) setOpenCount(res.totalElements);
      })
      .catch(() => {
        /* badge count is best-effort — leave the previous value on failure */
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);
  useSetNavBadge('notifications', openCount);

  // `setLoading(true)` lives at each state-changing call site (not inside
  // the fetch effect) — the react-hooks/set-state-in-effect rule flags a
  // synchronous setState in an effect body as a cascading-render risk. The
  // initial `useState(true)` default already covers the first mount.
  const refetch = () => {
    setLoading(true);
    setReloadToken((n) => n + 1);
  };

  const handleStatusTabChange = (key: string) => {
    setLoading(true);
    setStatusTab(key as StatusTab);
    setPage(0);
  };
  const handleSeverityChange = (value: string) => {
    setLoading(true);
    setSeverityFilter(value as SeverityFilter);
    setPage(0);
  };
  const handleTenantChange = (value: string) => {
    setLoading(true);
    setTenantFilter(value);
    setPage(0);
  };
  const handlePageChange = (next: number) => {
    setLoading(true);
    setPage(next);
  };

  const handleAcknowledge = async (id: number) => {
    setActionError(null);
    setBusyId(id);
    try {
      await acknowledge(id);
      refetch();
    } catch (err) {
      const wsrErr = wsrApiError(err);
      setActionError(
        wsrErr?.message ??
          t('notifications.ackError', 'Could not acknowledge this notification. Please try again.'),
      );
    } finally {
      setBusyId(null);
    }
  };

  // Memoized so a null `data` (before the first fetch settles) doesn't hand
  // `tenantOptions`'s useMemo a fresh `[]` identity on every render (mirrors
  // Queue.tsx's `jobs` memo).
  const content = useMemo(() => data?.content ?? [], [data]);
  const totalPages = data?.totalPages ?? 0;

  // Tenant options derived from the currently-loaded page — no separate
  // roster call (see file doc comment for the tradeoff).
  const tenantOptions = useMemo(() => {
    const slugs = new Set(content.filter((n) => n.tenantSlug !== '*').map((n) => n.tenantSlug));
    return Array.from(slugs).sort();
  }, [content]);

  const noFiltersActive = statusTab === 'ALL' && severityFilter === 'ALL' && tenantFilter === 'ALL';
  const emptyMessage = noFiltersActive
    ? t('notifications.empty', 'No notifications.')
    : t('notifications.emptyFiltered', 'No notifications match the current filters.');

  const tabs = [
    { key: 'ALL', label: t('notifications.tabs.all', 'All') },
    { key: 'OPEN', label: t('notifications.tabs.open', 'Open') },
    { key: 'ACKNOWLEDGED', label: t('notifications.tabs.acknowledged', 'Acknowledged') },
    { key: 'RESOLVED', label: t('notifications.tabs.resolved', 'Resolved') },
  ];

  return (
    <Box>
      <Typography variant="h1" component="h1" gutterBottom>
        {t('page.notifications.title', 'Notifications')}
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        {t('page.notifications.subtitle', 'Alerts and system notifications.')}
      </Typography>

      {loadFailed ? (
        <Alert
          severity="error"
          sx={{ mb: 2 }}
          action={
            <Typography
              component="button"
              variant="caption"
              onClick={refetch}
              sx={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                textDecoration: 'underline',
                color: 'inherit',
              }}
            >
              {t('common.retry', 'Retry')}
            </Typography>
          }
        >
          {t('notifications.loadError', 'Could not load notifications.')}
        </Alert>
      ) : null}

      {actionError ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setActionError(null)}>
          {actionError}
        </Alert>
      ) : null}

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{
          mb: 2,
          alignItems: { xs: 'stretch', sm: 'center' },
        }}
      >
        <TextField
          select
          size="small"
          label={t('notifications.filters.severityLabel', 'Severity')}
          value={severityFilter}
          onChange={(e) => handleSeverityChange(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="ALL">
            {t('notifications.filters.allSeverities', 'All severities')}
          </MenuItem>
          <MenuItem value="INFO">{t('severity.INFO', 'Info')}</MenuItem>
          <MenuItem value="WARN">{t('severity.WARN', 'Warning')}</MenuItem>
          <MenuItem value="ERROR">{t('severity.ERROR', 'Error')}</MenuItem>
        </TextField>

        <TextField
          select
          size="small"
          label={t('notifications.filters.tenantLabel', 'Tenant')}
          value={tenantFilter}
          onChange={(e) => handleTenantChange(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="ALL">{t('notifications.filters.allTenants', 'All tenants')}</MenuItem>
          <MenuItem value="*">{t('notifications.filters.platformOption', 'Platform (*)')}</MenuItem>
          {tenantOptions.map((slug) => (
            <MenuItem key={slug} value={slug}>
              {slug}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Box sx={{ mb: 2 }}>
        <FilterTabs
          tabs={tabs}
          active={statusTab}
          onChange={handleStatusTabChange}
          ariaLabel={t('notifications.filters.tabsAriaLabel', 'Filter by status')}
        />
      </Box>

      {loading ? <LoadingSpinner /> : null}

      {!loading ? (
        content.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            {emptyMessage}
          </Typography>
        ) : (
          <Stack spacing={2} aria-label={t('notifications.listAriaLabel', 'Notifications')}>
            {content.map((notification) => (
              <NotificationRow
                key={notification.id}
                notification={notification}
                onAcknowledge={(id) => void handleAcknowledge(id)}
                busy={busyId === notification.id}
              />
            ))}
          </Stack>
        )
      ) : null}

      {totalPages > 1 ? (
        <Box
          sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2, justifyContent: 'flex-end' }}
        >
          <Button
            size="small"
            onClick={() => handlePageChange(page - 1)}
            disabled={page <= 0 || loading}
            aria-label={t('notifications.prevPage', 'Previous page')}
          >
            {t('common.back', 'Previous')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('notifications.pageIndicator', 'Page {{page}} of {{total}}', {
              page: page + 1,
              total: totalPages,
            })}
          </Typography>
          <Button
            size="small"
            onClick={() => handlePageChange(page + 1)}
            disabled={page >= totalPages - 1 || loading}
            aria-label={t('notifications.nextPage', 'Next page')}
          >
            {t('common.next', 'Next')}
          </Button>
        </Box>
      ) : null}
    </Box>
  );
}
